import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Save } from 'lucide-react';

const EditPlan = () => {
  const { token, getAuthHeaders, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const { planId } = useParams();
  const isEditMode = !!planId;

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [autoGenerateFeatures, setAutoGenerateFeatures] = useState(true);
  const [featuresText, setFeaturesText] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: 0,
    billing_cycle: 'month',
    is_active: true,
    features: {
      appointment_limit: 25,
      service_limit: 1,
      fixed_calendar: true,
      floating_calendar: false,
      single_calendar: true,
      multiple_calendars: false,
      single_server: true,
      multiple_servers: false,
      email_confirmation: true,
      sms_confirmation: false,
      email_reminders: true,
      sms_reminders: false,
      marketing: false,
      custom_branding: false,
      reports: false,
      advanced_analytics: false,
      api_access: false,
      support_level: 'Basic'
    },
    display_features: [],
    auto_generate_display_features: true
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!isSuperAdmin()) {
      navigate('/dashboard');
      return;
    }
    if (isEditMode) {
      fetchPlan();
    }
  }, [planId]);

  const fetchPlan = async () => {
    try {
      const response = await fetch(`${API_URL}/api/plans/${planId}`, {
        headers: {
          ...getAuthHeaders()
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch plan');
      }

      const data = await response.json();
      
      // Check if plan has auto_generate_display_features setting
      const autoGenerate = data.auto_generate_display_features !== false;
      setAutoGenerateFeatures(autoGenerate);
      
      // If auto-generate is enabled, filter out auto-generated features from display_features
      let customFeatures = data.display_features || [];
      
      if (autoGenerate) {
        const autoFeaturePatterns = [
          /unlimited appointments/i,
          /\d+ appointments/i,
          /unlimited services/i,
          /up to \d+ services?/i,
          /\d+ services?/i,
          /fixed.*floating.*calendar/i,
          /^fixed calendar$/i,
          /^floating calendar$/i,
          /^single calendar$/i,
          /^multiple calendars?$/i,
          /multiple servers/i,
          /multiple staff/i,
          /^single server$/i,
          /^email confirmations?$/i,
          /^sms confirmations?$/i,
          /^email reminders?$/i,
          /^sms reminders?$/i,
          /^email & sms (confirmations?|reminders?)$/i,
          /marketing campaign/i,
          /^custom branding$/i,
          /^reports?( & insights)?$/i,
          /^reports & analytics$/i,
          /^advanced analytics$/i,
          /^api access$/i,
          /^(basic|priority|premium) support$/i,
          /customer online booking/i,
          /multiple services support/i
        ];
        
        customFeatures = customFeatures.filter(feature => {
          return !autoFeaturePatterns.some(pattern => pattern.test(feature));
        });
      }
      
      setFormData({
        ...data,
        display_features: customFeatures,
        auto_generate_display_features: autoGenerate
      });
      setFeaturesText(customFeatures.join('\n'));
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      const url = isEditMode
        ? `${API_URL}/api/plans/${planId}`
        : `${API_URL}/api/plans/`;
      
      const method = isEditMode ? 'PUT' : 'POST';

      // Parse manual features from textarea
      const manualFeatures = featuresText.split('\n').filter(line => line.trim() !== '');
      
      // Combine auto-generated features with custom features only if auto-generate is enabled
      const combinedDisplayFeatures = autoGenerateFeatures 
        ? [...generateAutoFeatures(), ...manualFeatures]
        : manualFeatures;

      const submitData = {
        ...formData,
        display_features: combinedDisplayFeatures,
        auto_generate_display_features: autoGenerateFeatures
      };

      const response = await fetch(url, {
        method,
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to save plan');
      }

      setSuccess(isEditMode ? 'Plan updated successfully!' : 'Plan created successfully!');
      setTimeout(() => {
        navigate('/admin/plans');
      }, 1500);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleFeatureChange = (featureName, value) => {
    let updatedFeatures = {
      ...formData.features,
      [featureName]: value
    };
    
    // Handle mutually exclusive pairs
    // Fixed Calendar and Floating Calendar are mutually exclusive
    if (featureName === 'fixed_calendar' && value === true) {
      updatedFeatures.floating_calendar = false;
    } else if (featureName === 'floating_calendar' && value === true) {
      updatedFeatures.fixed_calendar = false;
    }
    
    // Single Calendar and Multiple Calendars are mutually exclusive
    if (featureName === 'single_calendar' && value === true) {
      updatedFeatures.multiple_calendars = false;
    } else if (featureName === 'multiple_calendars' && value === true) {
      updatedFeatures.single_calendar = false;
    }
    
    // Single Server and Multiple Servers are mutually exclusive
    if (featureName === 'single_server' && value === true) {
      updatedFeatures.multiple_servers = false;
    } else if (featureName === 'multiple_servers' && value === true) {
      updatedFeatures.single_server = false;
    }
    
    setFormData({
      ...formData,
      features: updatedFeatures
    });
  };

  // Auto-generate feature text from configuration
  const generateAutoFeatures = () => {
    const features = formData.features;
    const autoFeatures = [];

    // Appointment limit
    if (features.appointment_limit === -1) {
      autoFeatures.push('Unlimited appointments');
    } else if (features.appointment_limit > 0) {
      autoFeatures.push(`${features.appointment_limit} appointments/month`);
    }

    // Service limit
    if (features.service_limit === -1) {
      autoFeatures.push('Unlimited services');
    } else if (features.service_limit > 0) {
      autoFeatures.push(`Up to ${features.service_limit} service${features.service_limit > 1 ? 's' : ''}`);
    }

    // Calendar type
    if (features.fixed_calendar && features.floating_calendar) {
      autoFeatures.push('Fixed & Floating calendars');
    } else if (features.fixed_calendar) {
      autoFeatures.push('Fixed calendar');
    } else if (features.floating_calendar) {
      autoFeatures.push('Floating calendar');
    }

    // Calendar count
    if (features.multiple_calendars) {
      autoFeatures.push('Multiple calendars');
    } else if (features.single_calendar) {
      autoFeatures.push('Single calendar');
    }

    // Server type
    if (features.multiple_servers) {
      autoFeatures.push('Multiple servers/staff');
    } else if (features.single_server) {
      autoFeatures.push('Single server');
    }

    // Notifications
    if (features.email_confirmation) {
      autoFeatures.push('Email confirmations');
    }
    if (features.sms_confirmation) {
      autoFeatures.push('SMS confirmations');
    }
    if (features.email_reminders) {
      autoFeatures.push('Email reminders');
    }
    if (features.sms_reminders) {
      autoFeatures.push('SMS reminders');
    }

    // Additional features
    if (features.marketing) {
      autoFeatures.push('Marketing campaigns');
    }
    if (features.custom_branding) {
      autoFeatures.push('Custom branding');
    }
    if (features.reports) {
      autoFeatures.push('Reports & insights');
    }
    if (features.advanced_analytics) {
      autoFeatures.push('Advanced analytics');
    }
    if (features.api_access) {
      autoFeatures.push('API access');
    }

    // Support level - always include
    if (features.support_level) {
      autoFeatures.push(`${features.support_level} support`);
    }

    return autoFeatures;
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex justify-center items-center h-screen">
          <div className="text-xl">Loading plan...</div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header />
      <section className="py-20 bg-gradient-to-br from-white to-amber-50 min-h-screen">
        <div className="max-w-4xl mx-auto px-6">
          <button
            onClick={() => navigate('/admin/plans')}
            className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-800"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Plans
          </button>

          <div className="bg-white rounded-lg shadow-xl p-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-6">
              {isEditMode ? 'Edit Plan' : 'Create New Plan'}
            </h1>

            {error && (
              <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                {error}
              </div>
            )}

            {success && (
              <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
                {success}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Plan Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 rounded-md border border-gray-300 focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 focus:outline-none"
                    placeholder="e.g., Pro"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Price ($/month) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
                    className="w-full px-4 py-3 rounded-md border border-gray-300 focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 focus:outline-none"
                    placeholder="14.95"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-3 rounded-md border border-gray-300 focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 focus:outline-none"
                  rows="3"
                  placeholder="Describe this plan..."
                  required
                />
              </div>

              {/* Features Configuration */}
              <div className="border-t pt-6">
                <h2 className="text-xl font-bold text-gray-800 mb-4">Feature Configuration</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Appointment Limit
                    </label>
                    <input
                      type="number"
                      value={formData.features.appointment_limit}
                      onChange={(e) => handleFeatureChange('appointment_limit', parseInt(e.target.value))}
                      className="w-full px-4 py-2 rounded-md border border-gray-300 focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 focus:outline-none"
                      placeholder="-1 for unlimited"
                    />
                    <p className="text-xs text-gray-500 mt-1">Use -1 for unlimited</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Service Limit
                    </label>
                    <input
                      type="number"
                      min="-1"
                      value={formData.features.service_limit}
                      onChange={(e) => handleFeatureChange('service_limit', parseInt(e.target.value))}
                      className="w-full px-4 py-2 rounded-md border border-gray-300 focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 focus:outline-none"
                      placeholder="-1 for unlimited"
                    />
                    <p className="text-xs text-gray-500 mt-1">Use -1 for unlimited, minimum 1</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Support Level
                    </label>
                    <select
                      value={formData.features.support_level}
                      onChange={(e) => handleFeatureChange('support_level', e.target.value)}
                      className="w-full px-4 py-2 rounded-md border border-gray-300 focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 focus:outline-none"
                    >
                      <option value="Basic">Basic</option>
                      <option value="Priority">Priority</option>
                      <option value="Premium">Premium</option>
                    </select>
                  </div>
                </div>

                {/* Feature Checkboxes */}
                <div className="space-y-6">
                  {/* Calendar Type - Mutually Exclusive */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm font-medium text-gray-700 mb-3">Calendar Type <span className="text-gray-400 font-normal">(select one)</span></p>
                    <div className="flex gap-6">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="calendarType"
                          checked={formData.features.fixed_calendar}
                          onChange={() => handleFeatureChange('fixed_calendar', true)}
                          className="w-4 h-4 text-yellow-500 border-gray-300 focus:ring-yellow-500"
                        />
                        <span className="text-sm text-gray-700">Fixed Calendar</span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="calendarType"
                          checked={formData.features.floating_calendar}
                          onChange={() => handleFeatureChange('floating_calendar', true)}
                          className="w-4 h-4 text-yellow-500 border-gray-300 focus:ring-yellow-500"
                        />
                        <span className="text-sm text-gray-700">Floating Calendar</span>
                      </label>
                    </div>
                  </div>

                  {/* Calendar Count - Mutually Exclusive */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm font-medium text-gray-700 mb-3">Calendar Count <span className="text-gray-400 font-normal">(select one)</span></p>
                    <div className="flex gap-6">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="calendarCount"
                          checked={formData.features.single_calendar}
                          onChange={() => handleFeatureChange('single_calendar', true)}
                          className="w-4 h-4 text-yellow-500 border-gray-300 focus:ring-yellow-500"
                        />
                        <span className="text-sm text-gray-700">Single Calendar</span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="calendarCount"
                          checked={formData.features.multiple_calendars}
                          onChange={() => handleFeatureChange('multiple_calendars', true)}
                          className="w-4 h-4 text-yellow-500 border-gray-300 focus:ring-yellow-500"
                        />
                        <span className="text-sm text-gray-700">Multiple Calendars</span>
                      </label>
                    </div>
                  </div>

                  {/* Server Type - Mutually Exclusive */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm font-medium text-gray-700 mb-3">Server Type <span className="text-gray-400 font-normal">(select one)</span></p>
                    <div className="flex gap-6">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="serverType"
                          checked={formData.features.single_server}
                          onChange={() => handleFeatureChange('single_server', true)}
                          className="w-4 h-4 text-yellow-500 border-gray-300 focus:ring-yellow-500"
                        />
                        <span className="text-sm text-gray-700">Single Server</span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="serverType"
                          checked={formData.features.multiple_servers}
                          onChange={() => handleFeatureChange('multiple_servers', true)}
                          className="w-4 h-4 text-yellow-500 border-gray-300 focus:ring-yellow-500"
                        />
                        <span className="text-sm text-gray-700">Multiple Servers</span>
                      </label>
                    </div>
                  </div>

                  {/* Notification Features */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm font-medium text-gray-700 mb-3">Notifications</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.features.email_confirmation}
                          onChange={(e) => handleFeatureChange('email_confirmation', e.target.checked)}
                          className="w-4 h-4 text-yellow-500 border-gray-300 rounded focus:ring-yellow-500"
                        />
                        <span className="text-sm text-gray-700">Email Confirmation</span>
                      </label>

                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.features.sms_confirmation}
                          onChange={(e) => handleFeatureChange('sms_confirmation', e.target.checked)}
                          className="w-4 h-4 text-yellow-500 border-gray-300 rounded focus:ring-yellow-500"
                        />
                        <span className="text-sm text-gray-700">SMS Confirmation</span>
                      </label>

                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.features.email_reminders}
                          onChange={(e) => handleFeatureChange('email_reminders', e.target.checked)}
                          className="w-4 h-4 text-yellow-500 border-gray-300 rounded focus:ring-yellow-500"
                        />
                        <span className="text-sm text-gray-700">Email Reminders</span>
                      </label>

                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.features.sms_reminders}
                          onChange={(e) => handleFeatureChange('sms_reminders', e.target.checked)}
                          className="w-4 h-4 text-yellow-500 border-gray-300 rounded focus:ring-yellow-500"
                        />
                        <span className="text-sm text-gray-700">SMS Reminders</span>
                      </label>
                    </div>
                  </div>

                  {/* Other Features */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm font-medium text-gray-700 mb-3">Additional Features</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.features.marketing}
                          onChange={(e) => handleFeatureChange('marketing', e.target.checked)}
                          className="w-4 h-4 text-yellow-500 border-gray-300 rounded focus:ring-yellow-500"
                        />
                        <span className="text-sm text-gray-700">Marketing (Email/SMS)</span>
                      </label>

                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.features.custom_branding}
                          onChange={(e) => handleFeatureChange('custom_branding', e.target.checked)}
                          className="w-4 h-4 text-yellow-500 border-gray-300 rounded focus:ring-yellow-500"
                        />
                        <span className="text-sm text-gray-700">Custom Branding</span>
                      </label>

                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.features.reports}
                          onChange={(e) => handleFeatureChange('reports', e.target.checked)}
                          className="w-4 h-4 text-yellow-500 border-gray-300 rounded focus:ring-yellow-500"
                        />
                        <span className="text-sm text-gray-700">Reports</span>
                      </label>

                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.features.advanced_analytics}
                          onChange={(e) => handleFeatureChange('advanced_analytics', e.target.checked)}
                          className="w-4 h-4 text-yellow-500 border-gray-300 rounded focus:ring-yellow-500"
                        />
                        <span className="text-sm text-gray-700">Advanced Analytics</span>
                      </label>

                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.features.api_access}
                          onChange={(e) => handleFeatureChange('api_access', e.target.checked)}
                          className="w-4 h-4 text-yellow-500 border-gray-300 rounded focus:ring-yellow-500"
                        />
                        <span className="text-sm text-gray-700">API Access</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Display Features */}
              <div className="border-t pt-6">
                <h2 className="text-xl font-bold text-gray-800 mb-4">Display Features</h2>
                <p className="text-sm text-gray-600 mb-4">
                  Features shown to users on the pricing page.
                </p>

                {/* Auto-generate Toggle */}
                <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <label className="flex items-center justify-between cursor-pointer">
                    <div>
                      <span className="text-sm font-medium text-gray-700">Auto-generate from configuration</span>
                      <p className="text-xs text-gray-500 mt-1">
                        Automatically create feature list based on plan settings above
                      </p>
                    </div>
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={autoGenerateFeatures}
                        onChange={(e) => setAutoGenerateFeatures(e.target.checked)}
                        className="sr-only"
                      />
                      <div className={`w-11 h-6 rounded-full transition-colors ${autoGenerateFeatures ? 'bg-yellow-500' : 'bg-gray-300'}`}>
                        <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${autoGenerateFeatures ? 'translate-x-5' : 'translate-x-0'}`}></div>
                      </div>
                    </div>
                  </label>
                </div>

                {/* Auto-generated Features Preview - only show if enabled */}
                {autoGenerateFeatures && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <p className="text-sm font-medium text-blue-800 mb-2">Auto-generated from configuration:</p>
                    <ul className="space-y-1">
                      {generateAutoFeatures().map((feature, index) => (
                        <li key={index} className="text-sm text-blue-700 flex items-center">
                          <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Custom/Manual Features */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    {autoGenerateFeatures ? 'Additional Custom Features:' : 'Feature List (Manual):'}
                  </p>
                  <p className="text-xs text-gray-500 mb-3">
                    Enter one feature per line
                  </p>
                  
                  <textarea
                    value={featuresText}
                    onChange={(e) => setFeaturesText(e.target.value)}
                    onBlur={(e) => {
                      // Parse lines and update formData on blur
                      const lines = e.target.value.split('\n').filter(line => line.trim() !== '');
                      setFormData({
                        ...formData,
                        display_features: lines
                      });
                      // Also clean up the textarea (remove empty lines)
                      setFeaturesText(lines.join('\n'));
                    }}
                    className="w-full px-4 py-3 rounded-md border border-gray-300 focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 focus:outline-none font-mono text-sm"
                    rows={6}
                    placeholder={autoGenerateFeatures 
                      ? "24/7 phone support\nFree onboarding\nDedicated account manager" 
                      : "Unlimited appointments\nEmail confirmations\nPriority support"}
                  />
                  
                  <p className="text-xs text-gray-400 mt-2">
                    {featuresText.split('\n').filter(f => f.trim()).length} feature(s)
                  </p>
                </div>

                {/* Combined Preview */}
                <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-green-800 mb-2">Preview (what users will see):</p>
                  <ul className="space-y-1">
                    {(() => {
                      const manualFeatures = featuresText.split('\n').filter(f => f.trim());
                      const allFeatures = autoGenerateFeatures 
                        ? [...generateAutoFeatures(), ...manualFeatures] 
                        : manualFeatures;
                      return allFeatures.map((feature, index) => (
                        <li key={index} className="text-sm text-green-700 flex items-center">
                          <span className="mr-2">✓</span>
                          {feature}
                        </li>
                      ));
                    })()}
                    {!autoGenerateFeatures && featuresText.split('\n').filter(f => f.trim()).length === 0 && (
                      <li className="text-sm text-gray-400 italic">No features to display</li>
                    )}
                  </ul>
                </div>
              </div>

              {/* Status */}
              <div className="border-t pt-6">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 text-yellow-500 border-gray-300 rounded focus:ring-yellow-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Active (visible to users)</span>
                </label>
              </div>

              {/* Submit Button */}
              <div className="flex gap-3 pt-6">
                <button
                  type="button"
                  onClick={() => navigate('/admin/plans')}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold px-6 py-3 rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-yellow-500 hover:bg-yellow-600 disabled:bg-yellow-300 text-white font-semibold px-6 py-3 rounded-md transition-colors flex items-center justify-center gap-2"
                >
                  <Save className="w-5 h-5" />
                  {saving ? 'Saving...' : (isEditMode ? 'Update Plan' : 'Create Plan')}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default EditPlan;
